import { 
  doc, 
  getDoc, 
  setDoc, 
  onSnapshot, 
  serverTimestamp,
  writeBatch,
  deleteField,
  collection,
  getDocs
} from "firebase/firestore";

/**
 * Valores por defecto para Profiles
 */
export const DEFAULT_PROFILES = [
  { id: 'Perfil', name: 'Perfil', whatsapp: '', requireConfirmationWhenMarking: true }
];

/**
 * Normaliza un número de WhatsApp:
 * - Quita espacios, guiones, paréntesis
 * - Conserva el +
 * Retorna: { normalized: 'número limpio', forWame: 'número sin +' }
 */
export function normalizePhoneNumber(phone) {
  if (!phone || typeof phone !== 'string') {
    return { normalized: '', forWame: '' };
  }

  // Quitar espacios, guiones, paréntesis
  const cleaned = phone.replace(/[\s\-\(\)]/g, '');
  
  // Extraer números y +
  const normalized = cleaned.replace(/[^\d\+]/g, '');
  
  // Versión para wa.me (sin +)
  const forWame = normalized.replace(/\+/g, '');

  return { normalized, forWame };
}

/**
 * Valida un número de WhatsApp
 * Retorna: { valid: boolean, message: string }
 */
export function validatePhoneNumber(phone) {
  if (!phone || phone.trim() === '') {
    return { valid: true, message: '' }; // Permitir vacío
  }

  // Permitir números, espacios, +, guiones, paréntesis
  if (!/^[\d\s\+\-\(\)]+$/.test(phone)) {
    return { 
      valid: false, 
      message: 'El teléfono solo puede contener números, +, espacios, guiones y paréntesis.' 
    };
  }

  const { normalized } = normalizePhoneNumber(phone);
  
  // Mínimo 9 dígitos
  const digitCount = normalized.replace(/\D/g, '').length;
  if (digitCount < 9) {
    return { 
      valid: false, 
      message: 'El teléfono debe tener al menos 9 dígitos.' 
    };
  }

  return { valid: true, message: '' };
}

/**
 * Normaliza los datos del documento Firestore
 */
function normalizeProfiles(docData) {
   if (!docData || !Array.isArray(docData.profiles)) {
     return DEFAULT_PROFILES;
   }

   return docData.profiles.map(profile => ({
     id: profile.id || profile.name || '',
     name: profile.name || '',
     whatsapp: profile.whatsapp || '',
     webUrl: profile.webUrl || '',
     imageUrl: profile.imageUrl || '',
     requireConfirmationWhenMarking: profile.requireConfirmationWhenMarking ?? true,
     status: profile.status ?? 'active',
     ...(profile.deletionScheduledAt ? { deletionScheduledAt: profile.deletionScheduledAt } : {}),
     ...(profile.pinHash ? { pinHash: profile.pinHash } : {}),
     ...(profile.pinSalt ? { pinSalt: profile.pinSalt } : {}),
     ...(profile.pinIterations ? { pinIterations: profile.pinIterations } : {}),
     ...(profile.pinVersion ? { pinVersion: profile.pinVersion } : {}),
     ...(profile.pinUpdatedAt ? { pinUpdatedAt: profile.pinUpdatedAt } : {}),
   }));
 }


/**
 * Inicializa la configuración de Profiles
 * Si no existe, crea el documento con valores por defecto
 */
export async function initializeProfiles(workspaceId, db, userId) {
  if (!workspaceId || !db) {
    console.error("[PROFILES_SYNC] Invalid workspaceId or db");
    return DEFAULT_PROFILES;
  }

  try {
    const profilesRef = doc(db, "workspaces", workspaceId, "settings", "profiles");
    console.log("[PROFILES_SYNC] Initializing with ref:", profilesRef.path);
    const profilesSnap = await getDoc(profilesRef);

    if (profilesSnap.exists()) {
      console.log("[PROFILES_SYNC] ✅ Profiles loaded from Firestore");
      return normalizeProfiles(profilesSnap.data());
    }

    // No existe, crear con valores por defecto
    console.log("[PROFILES_SYNC] Profiles not found, creating with defaults...");
    const defaultData = {
      profiles: DEFAULT_PROFILES,
      updatedAt: serverTimestamp(),
      updatedBy: userId || 'system',
    };

    await setDoc(profilesRef, defaultData);
    console.log("[PROFILES_SYNC] ✅ Default profiles created successfully");
    return normalizeProfiles(defaultData);
  } catch (error) {
    console.error("[PROFILES_SYNC] ❌ Error initializing profiles:", error);
    return DEFAULT_PROFILES;
  }
}

/**
 * Suscribe a cambios en tiempo real del documento de profiles
 * Retorna una función para desuscribirse
 * 🔒 BLINDADO: safe callback pattern + diagnostic logs
 */
export function subscribeToProfiles(workspaceId, db, onProfilesChange) {
  if (!workspaceId || !db) {
    console.error("[PROFILES_SYNC] Invalid workspaceId or db");
    return () => {};
  }

  // 🔒 Safe callback pattern: crear versión segura de onProfilesChange
  const safeOnProfilesChange = typeof onProfilesChange === "function" 
    ? onProfilesChange 
    : () => {
        console.warn("[PROFILES_SYNC] onProfilesChange is not a function, using no-op");
      };

  // 📊 Diagnostic logs
  console.log("[PROFILES_SYNC_DBG] typeof onProfilesChange =", typeof onProfilesChange);
  console.log("[PROFILES_SYNC_DBG] onProfilesChange =", onProfilesChange);

  try {
    const profilesRef = doc(db, "workspaces", workspaceId, "settings", "profiles");
    console.log("[PROFILES_SYNC] Subscribing to:", profilesRef.path);
    
     const unsubscribe = onSnapshot(
       profilesRef,
       (snapshot) => {
         if (snapshot.exists()) {
           const normalized = normalizeProfiles(snapshot.data());
           console.log("[PROFILES_SYNC] 📡 Profiles updated from Firestore:", normalized);
           safeOnProfilesChange(normalized);
         } else {
           // El doc no existe: inicializar con DEFAULT_PROFILES inmediatamente
           // Notificar al callback sin esperar a la persistencia (desbloquea AppReadyGate)
           console.log("[PROFILES_SYNC] Profiles doc not found, initializing with defaults");
           safeOnProfilesChange(DEFAULT_PROFILES);
           
           // Persistir en background de forma no-bloqueante
           initializeProfiles(workspaceId, db, null)
             .then(() => {
               console.log("[PROFILES_SYNC] ✅ Default profiles persisted to Firestore");
             })
             .catch((err) => {
               console.error("[PROFILES_SYNC] Failed to persist default profiles:", err);
             });
         }
       },
      (error) => {
        console.error("[PROFILES_SYNC] ❌ Subscription error:", error);
      }
    );

    console.log("[PROFILES_SYNC] ✅ Subscription active");
    return unsubscribe;
  } catch (error) {
    console.error("[PROFILES_SYNC] ❌ Error subscribing to profiles:", error);
    return () => {};
  }
}

/**
 * Actualiza los profiles en Firestore
 * Normaliza los teléfonos antes de guardar
 */
export async function updateProfiles(workspaceId, db, profiles, userId) {
  if (!workspaceId || !db || !Array.isArray(profiles)) {
    console.error("[PROFILES_SYNC] Invalid parameters for update");
    return false;
  }

  try {
    const profilesRef = doc(db, "workspaces", workspaceId, "settings", "profiles");
    console.log("[PROFILES_SYNC] Updating profiles at:", profilesRef.path);
    
     // Normalizar teléfonos antes de guardar y preservar campos de PIN
     const normalizedProfiles = profiles.map(profile => ({
       id: profile.id || profile.name || '',
       name: profile.name || '',
       whatsapp: normalizePhoneNumber(profile.whatsapp || '').normalized,
       webUrl: profile.webUrl || '',
       imageUrl: profile.imageUrl || '',
       requireConfirmationWhenMarking: profile.requireConfirmationWhenMarking ?? false,
       status: profile.status ?? 'active',
       ...(profile.deletionScheduledAt ? { deletionScheduledAt: profile.deletionScheduledAt } : {}),
       ...(profile.pinHash ? { pinHash: profile.pinHash } : {}),
       ...(profile.pinSalt ? { pinSalt: profile.pinSalt } : {}),
       ...(profile.pinIterations ? { pinIterations: profile.pinIterations } : {}),
       ...(profile.pinVersion ? { pinVersion: profile.pinVersion } : {}),
       ...(profile.pinUpdatedAt ? { pinUpdatedAt: profile.pinUpdatedAt } : {}),
     }));


     const dataToUpdate = {
       profiles: normalizedProfiles,
       updatedAt: serverTimestamp(),
       updatedBy: userId || 'unknown',
     };

     // Usar writeBatch para actualizar settings/profiles y workerProfiles atómicamente
     const batch = writeBatch(db);
     batch.set(profilesRef, dataToUpdate, { merge: true });
      // Limpiar documentos obsoletos de workerProfiles
      // Leer la colección existente para identificar perfiles que ya no existen
      const workerProfilesRef = collection(db, "workspaces", workspaceId, "workerProfiles");
      const existingDocs = await getDocs(workerProfilesRef);
      const currentProfileIds = new Set(normalizedProfiles.map(p => p.id));
      
      // Agregar batch.delete para perfiles cuya ID ya no existe en normalizedProfiles
      existingDocs.forEach(docSnap => {
        if (!currentProfileIds.has(docSnap.id)) {
          batch.delete(docSnap.ref);
          console.log(`[PROFILES_SYNC] Marking for deletion: workerProfiles/${docSnap.id}`);
        }
      });



     // Proyectar cada perfil a workerProfiles/{profileId}
     // PIN: workerProfiles almacena los 5 campos como campos DIRECTOS del documento
     // (a diferencia del array profiles de settings/profiles). Como se escribe con
     // merge:true, omitir un campo NO lo borra — se requiere deleteField() explícito
     // para eliminar realmente un PIN que ya no existe en el perfil de origen.
     const pinKeys = ['pinHash', 'pinSalt', 'pinIterations', 'pinVersion', 'pinUpdatedAt'];
     for (const profile of normalizedProfiles) {
       const workerProfileRef = doc(db, "workspaces", workspaceId, "workerProfiles", profile.id);
       const workerProfileData = {
         id: profile.id,
         name: profile.name,
         status: profile.status,
         whatsapp: profile.whatsapp,
         webUrl: profile.webUrl || '',
         imageUrl: profile.imageUrl || '',
         requireConfirmationWhenMarking: profile.requireConfirmationWhenMarking,
       };

       const hasCompletePin = pinKeys.every((key) => profile[key] !== undefined && profile[key] !== null);
       const hasNoPin = pinKeys.every((key) => profile[key] === undefined || profile[key] === null);

       if (hasCompletePin) {
         workerProfileData.pinHash = profile.pinHash;
         workerProfileData.pinSalt = profile.pinSalt;
         workerProfileData.pinIterations = profile.pinIterations;
         workerProfileData.pinVersion = profile.pinVersion;
         workerProfileData.pinUpdatedAt = profile.pinUpdatedAt;
       } else if (hasNoPin) {
         workerProfileData.pinHash = deleteField();
         workerProfileData.pinSalt = deleteField();
         workerProfileData.pinIterations = deleteField();
         workerProfileData.pinVersion = deleteField();
         workerProfileData.pinUpdatedAt = deleteField();
       } else {
         // Estado parcial: no debería ocurrir dado que normalizedProfiles ya
         // construye los 5 campos condicionalmente juntos. Se omite la
         // proyección del PIN para evitar escribir un estado parcial.
         console.warn("[PROFILES_SYNC] Partial PIN state detected for profile, skipping PIN projection:", profile.id);
       }

       batch.set(workerProfileRef, workerProfileData, { merge: true });
     }

     await batch.commit();
     console.log("[PROFILES_SYNC] ✅ Profiles updated in Firestore (settings + workerProfiles):", dataToUpdate);
     return true;
  } catch (error) {
    console.error("[PROFILES_SYNC] ❌ Error updating profiles:", error);
    console.error("[PROFILES_SYNC] Error details:", error.message);
    return false;
  }
}
